import { copilotQuery, copilotRequest } from "./copilot";

// Admin side of LUD Copilot (backend: /api/auth/copilot/admin). Always the
// signed-in session; the server checks the permission and the conversation
// scope on every call, so these never assume what the UI hid.

const base = (id) => `admin/conversations/${encodeURIComponent(id)}`;

// params: status, category, assignedTo (me | unassigned | <user id>), search,
// from, to (ISO dates), unread ("true"), cursor, limit.
export const listAdminConversationsAPI = (params = {}, signal) =>
  copilotRequest(`admin/conversations${copilotQuery(params)}`, { signal });

export const getAdminConversationAPI = (id, { before, limit = 30, signal } = {}) =>
  copilotRequest(`${base(id)}${copilotQuery({ before, limit })}`, { signal });

// internal: true writes a staff-only note instead of a customer-visible reply.
export const replyAdminConversationAPI = (id, { text, attachments, internal, clientMessageId }) =>
  copilotRequest(`${base(id)}/messages`, {
    method: "POST",
    idempotencyKey: clientMessageId,
    body: { ...(text ? { text } : {}), ...(attachments?.length ? { attachments } : {}), ...(internal ? { internal: true } : {}) },
  });

export const assignAdminConversationAPI = (id, assigneeId) =>
  copilotRequest(`${base(id)}/assign`, { method: "POST", body: { assigneeId } });

// status: open | pending | resolved (resolved/reopen are plain status changes).
export const setAdminConversationStatusAPI = (id, status, note) =>
  copilotRequest(`${base(id)}/status`, { method: "POST", body: { status, ...(note ? { note } : {}) } });

export const markAdminConversationReadAPI = (id) => copilotRequest(`${base(id)}/read`, { method: "POST", body: {} });

export const uploadAdminAttachmentAPI = (id, file) => {
  const form = new FormData();
  form.append("file", file);
  return copilotRequest(`${base(id)}/attachments`, { method: "POST", form });
};

// -> { unresolved, unreadConversations }  unresolved = open + pending
export const fetchCopilotUnresolvedAPI = (signal) => copilotRequest("admin/unresolved-count", { signal });
// -> { stats: { total, byStatus, unresolved, unassigned, unreadByAdmin, byCategory,
//      averageFirstResponseMinutes, averageResolutionMinutes, unresolvedByAgent, last7Days, createdPerDay } }
export const fetchCopilotStatsAPI = (signal) => copilotRequest("admin/stats", { signal });
// -> { agents: [{ id, name, role }] }  (assignment right required)
export const fetchCopilotAgentsAPI = (signal) => copilotRequest("admin/agents", { signal });

// ---- saved replies ---------------------------------------------------------------------
// Everyone who may reply can list and use them; only managers change them.
// -> { replies: [{ id, title, body, lang: any|ar|en, category, usageCount }] }
// settings (automatic first replies)
export const fetchCopilotSettingsAPI = (signal) => copilotRequest("admin/settings", { signal });
export const updateAutoReplySettingsAPI = (body) => copilotRequest("admin/settings/auto-reply", { method: "PUT", body });

export const listSavedRepliesAPI = (params = {}, signal) => copilotRequest(`admin/saved-replies${copilotQuery(params)}`, { signal });
export const createSavedReplyAPI = (body) => copilotRequest("admin/saved-replies", { method: "POST", body });
export const updateSavedReplyAPI = (id, body) => copilotRequest(`admin/saved-replies/${encodeURIComponent(id)}`, { method: "PUT", body });
export const archiveSavedReplyAPI = (id) => copilotRequest(`admin/saved-replies/${encodeURIComponent(id)}`, { method: "DELETE" });
export const markSavedReplyUsedAPI = (id) => copilotRequest(`admin/saved-replies/${encodeURIComponent(id)}/use`, { method: "POST", body: {} });

// ---- FAQs (copilot.reply lists, copilot.manage_faqs changes) --------------------------------
// params: search, category, visibility (public|authenticated), status (active|inactive), source (system|custom)
// -> { faqs: [{ id, key, category, visibility, question, answer, active, order, source, edited, stats }], categories }
export const listAdminFaqsAPI = (params = {}, signal) => copilotRequest(`admin/faqs${copilotQuery(params)}`, { signal });
export const createAdminFaqAPI = (body) => copilotRequest("admin/faqs", { method: "POST", body });
export const updateAdminFaqAPI = (id, body) => copilotRequest(`admin/faqs/${encodeURIComponent(id)}`, { method: "PUT", body });
export const deleteAdminFaqAPI = (id) => copilotRequest(`admin/faqs/${encodeURIComponent(id)}`, { method: "DELETE" });
export const reorderAdminFaqsAPI = (ids) => copilotRequest("admin/faqs/reorder", { method: "POST", body: { ids } });
